import { useState, useEffect, useCallback } from "react";

/**
 * Breakpoints aligned with Tailwind CSS defaults + project custom breakpoints.
 * sm: 640px, md: 768px, lg: 1024px, xl: 1280px, 2xl: 1536px, 3xl: 1600px
 */
const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
  "3xl": 1600,
};

function getDeviceInfo(width) {
  return {
    width,
    isMobile: width < BREAKPOINTS.sm, // < 640px
    isTablet: width >= BREAKPOINTS.sm && width < BREAKPOINTS.lg, // 640px - 1023px
    isDesktop: width >= BREAKPOINTS.lg, // >= 1024px
    isSmUp: width >= BREAKPOINTS.sm, // >= 640px
    isMdUp: width >= BREAKPOINTS.md, // >= 768px
    isLgUp: width >= BREAKPOINTS.lg, // >= 1024px
    isXlUp: width >= BREAKPOINTS.xl, // >= 1280px
    is2xlUp: width >= BREAKPOINTS["2xl"], // >= 1536px
    is3xlUp: width >= BREAKPOINTS["3xl"], // >= 1600px
  };
}

/**
 * Hook that provides device/viewport information with debounced resize handling.
 * Use this instead of inline `window.innerWidth` checks for consistent breakpoints.
 *
 * @example
 * const { isMobile, isTablet, isDesktop } = useDevice();
 * <Drawer width={isMobile ? "100%" : isTablet ? "80%" : "40%"} />
 */
export function useDevice() {
  const [device, setDevice] = useState(() => getDeviceInfo(window.innerWidth));

  const handleResize = useCallback(() => {
    setDevice(getDeviceInfo(window.innerWidth));
  }, []);

  useEffect(() => {
    let timeoutId;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 150);
    };

    window.addEventListener("resize", debouncedResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", debouncedResize);
    };
  }, [handleResize]);

  return device;
}

export { BREAKPOINTS };
